import api from '../../services/api';

export const getOrderDetailRequest = async (orderId) => {
  return await api.get(`order_detail/${orderId}/`);
};

/**
 * Updates one section of an order.
 *
 * The backend dispatches on `action`: `"shipping"` rewrites the delivery block
 * (method, warehouse, address, weight, packages), `"recipient"` rewrites the
 * contact block. Sending the wrong one silently updates nothing, so both
 * payloads are built by dedicated helpers below rather than by callers.
 *
 * @param {object} payload
 */
export const updateOrderInfoRequest = async (payload) => {
  return await api.put('update_order_info/', payload);
};

/**
 * Overwrites the status of the order AND of every delivery item under it.
 *
 * @param {number|string} orderId
 * @param {{status: string, substatus: string, details: string, image_urls: string[]}} payload
 */
export const updateOrderStateRequest = async (orderId, payload) => {
  return await api.patch(`update_order_state/${orderId}/`, payload);
};

/**
 * Edits the free-text detail of one event already in the status history.
 *
 * Keyed by the delivery, not by the order.
 *
 * @param {number|string} deliveryId
 * @param {number|string} statusId
 * @param {string} details
 */
export const updateStatusHistoryRequest = async (deliveryId, statusId, details) => {
  return await api.patch(`deliveries/${deliveryId}/update_status_history/${statusId}/`, {
    details,
  });
};

/** Marks the delivery as prepared (`fulfilled`). */
export const fulfillDeliveryRequest = async (deliveryId) => {
  return await api.patch(`deliveries/${deliveryId}/fulfill/`, {});
};

/**
 * Forces tracking creation and dispatch, taking the delivery out of the hold
 * some clients keep their orders in.
 */
export const dispatchPreparedRequest = async (deliveryId) => {
  return await api.post(`deliveries/${deliveryId}/dispatch_prepared/`);
};

/**
 * Drivers the company can dispatch with.
 *
 * Only meaningful on the `admin_checkout` plan, which is the one that assigns
 * its own fleet instead of handing the parcel to a courier.
 *
 * @param {number|string} [adminId] - Whose fleet. Only the internal team may
 *   name another operator; for anybody else the backend ignores it.
 */
export const getDriversRequest = async (adminId) => {
  // With the slash: the route is `get-all/`, so without it every call pays a
  // 301 from `APPEND_SLASH` before the real request.
  return await api.get('drivers/get-all/', {
    params: { page: 1, per: 200, ...(adminId ? { admin_id: adminId } : {}) },
  });
};

/** Warehouses the seller's company can dispatch from. */
export const getCompanyWarehousesRequest = async (companyId) => {
  return await api.get(`warehouses/get_company_warehouses/${companyId}/`);
};

/** Couriers the signed-in company is allowed to route through. */
export const getAvailableCouriersRequest = async () => {
  return await api.get('companies/available_couriers/');
};

/**
 * Reassigns the delivery to another courier.
 *
 * A response of `{status: "dispatching"}` means the courier has no stand-by
 * window, so the parcel would leave for the TMS immediately — the caller is
 * expected to confirm with the operator before treating it as done.
 */
export const changeCourierRequest = async (deliveryId, courier) => {
  return await api.patch(`deliveries/${deliveryId}/change_courier/`, { courier });
};

/** Registers how many units of an order line have been picked. */
export const prepareOrderLineItemRequest = async (orderProductId, preparedQuantity) => {
  return await api.patch(`order_line_items/${orderProductId}/prepare/`, {
    prepared_quantity: preparedQuantity,
  });
};

/**
 * Uploads one proof-of-delivery image and returns its stored URL.
 *
 * The multipart header is not optional: `api` defaults to JSON, and axios turns
 * a `FormData` sent under that header into JSON — the file is dropped and the
 * view, which only parses multipart, answers 415.
 */
export const uploadProofOfDeliveryRequest = async (file, filename, onUploadProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('filename', filename);
  return await api.post('upload_pod/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress,
  });
};

export const deleteProofOfDeliveryRequest = async (url) => {
  return await api.delete('delete_pod/', { data: { url } });
};
